import { EntityType } from '~/enums/EntityType';
import { ModelVersion } from '~/features/x-ray/types/model';
import { TableExtractionConfigState } from '~/features/x-ray/types/table';
import { sylvanus } from '~/services/serviceInstances';
import { Entity } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import {
  PromptModel,
  PromptModelConfig,
  TableExtractionConfigCreate,
} from '../../types/sylvanus/model';
import { createPromptModelVersion } from './createPromptModelVersion';
import { withQueryFormat } from './searchScopeUtils';
import { createFirstTestCase } from './testCaseUtils';
import {
  mapPromptModelVersionToTableModelVersion,
  mapTableColumnSpecState,
} from './transformers';

interface Request {
  entity: Entity<EntityType>;
}
type Response = PromptModel;

const createPromptModel = async ({ entity }: Request): Promise<Uuid> => {
  const response = (await sylvanus.post('/model', {
    target_entity: entity,
  })) as Response;

  return response.id;
};

export const saveTableModelDraft = async ({
  modelId,
  config,
  highlightMethod,
}: {
  modelId: Nullable<Uuid>;
  config: TableExtractionConfigState;
  highlightMethod: string;
}): Promise<ModelVersion> => {
  if (!modelId) {
    modelId = await createPromptModel({
      entity: {
        id: config.tableSpecState.key,
        type: EntityType.DocumentAttachedData,
      },
    });
  }

  const configParams: TableExtractionConfigCreate = {
    input_type: 'evisort_document',
    instructions: config.instructions,
    internal: config.internal ?? null,
    output_type: 'table_value',
    prompt_model_type: 'table_extraction',
    scope: {
      data: {
        attributes: {
          filters: [],
          query: withQueryFormat(config.query),
          store_as_recent_search: false,
        },
      },
      type: 'document',
    },
    table_spec: {
      name: config.tableSpecState.name,
      columns: config.tableSpecState.columns.map(mapTableColumnSpecState),
    },
  };

  const response = await createPromptModelVersion({
    modelId,
    config: configParams as PromptModelConfig,
  });

  const modelVersion = mapPromptModelVersionToTableModelVersion({
    modelId,
    version: response,
    highlightMethod,
    publishStatus: null,
  });

  try {
    await createFirstTestCase(modelVersion, highlightMethod);
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail, { cause: modelId });
    } else {
      throw error;
    }
  }

  return modelVersion;
};
